import type { Layout } from 'react-grid-layout';

/**
 * Dashboard kart kaydı (registry).
 *
 * Yeni bir kart eklemek için:
 *  1. Buraya bir kayıt ekleyin (id + titleKey + group + defaultLayout)
 *  2. Dashboard.tsx içindeki renderCard map'ine ilgili JSX'i ekleyin
 *  3. tr/en accounting.json'a başlık çevirisini ekleyin
 * Görünüm ayarları modalı ve grid düzeni otomatik olarak yeni kartı tanır.
 */

export type DashboardCardGroup = 'stats' | 'widget' | 'chart';

export interface DashboardCardDef {
  id: string;
  /** accounting namespace'inde i18n anahtarı (görünüm ayarları modalında gösterilir) */
  titleKey: string;
  group: DashboardCardGroup;
  defaultLayout: Omit<Layout, 'i'>;
  /**
   * true ise kart varsayılan GİZLİ gelir (hem yeni hem mevcut kullanıcılarda);
   * kullanıcı görünüm ayarları modalından açarsa görünür.
   */
  defaultHidden?: boolean;
}

/** Grid kolon sayısı (masaüstü) */
export const DASHBOARD_GRID_COLS = 12;
/** Grid satır yüksekliği (px) */
export const DASHBOARD_ROW_HEIGHT = 45;
/** Kartlar arası boşluk (px) */
export const DASHBOARD_GRID_MARGIN: [number, number] = [8, 8];

// Varsayılan dizilim mevcut sabit düzeni birebir korur:
// 1. satır: 4 istatistik kartı (w=3, h=2)
// 2. satır: 4 widget (w=3, h=8)
// 3. satır: 4 grafik (w=3, h=7)
export const DASHBOARD_CARDS: DashboardCardDef[] = [
  // ===== İSTATİSTİK KARTLARI =====
  {
    id: 'statsSales',
    titleKey: 'dashboard.cards.statsSales',
    group: 'stats',
    defaultLayout: { x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  },
  {
    id: 'statsStock',
    titleKey: 'dashboard.cards.statsStock',
    group: 'stats',
    defaultLayout: { x: 3, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  },
  {
    id: 'statsCashbox',
    titleKey: 'dashboard.cards.statsCashbox',
    group: 'stats',
    defaultLayout: { x: 6, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  },
  {
    id: 'statsBank',
    titleKey: 'dashboard.cards.statsBank',
    group: 'stats',
    defaultLayout: { x: 9, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  },
  {
    id: 'statsScreenTopSelling',
    titleKey: 'dashboard.cards.statsScreenTopSelling',
    group: 'stats',
    defaultLayout: { x: 0, y: 17, w: 3, h: 2, minW: 2, minH: 2 },
    defaultHidden: true,
  },
  {
    id: 'statsRestaurantScreenTopSelling',
    titleKey: 'dashboard.cards.statsRestaurantScreenTopSelling',
    group: 'stats',
    defaultLayout: { x: 0, y: 25, w: 3, h: 2, minW: 2, minH: 2 },
    defaultHidden: true,
  },
  {
    id: 'statsPackageScreenTopSelling',
    titleKey: 'dashboard.cards.statsPackageScreenTopSelling',
    group: 'stats',
    defaultLayout: { x: 0, y: 33, w: 3, h: 2, minW: 2, minH: 2 },
    defaultHidden: true,
  },

  // ===== WIDGET'LAR =====
  {
    id: 'widgetBranchSales',
    titleKey: 'dashboard.cards.widgetBranchSales',
    group: 'widget',
    defaultLayout: { x: 0, y: 2, w: 3, h: 8, minW: 2, minH: 4 },
  },
  {
    id: 'widgetBestSelling',
    titleKey: 'dashboard.cards.widgetBestSelling',
    group: 'widget',
    defaultLayout: { x: 3, y: 2, w: 3, h: 8, minW: 2, minH: 4 },
  },
  {
    id: 'widgetCashboxes',
    titleKey: 'dashboard.cards.widgetCashboxes',
    group: 'widget',
    defaultLayout: { x: 6, y: 2, w: 3, h: 8, minW: 2, minH: 4 },
  },
  {
    id: 'widgetBanks',
    titleKey: 'dashboard.cards.widgetBanks',
    group: 'widget',
    defaultLayout: { x: 9, y: 2, w: 3, h: 8, minW: 2, minH: 4 },
  },
  {
    id: 'widgetScreenTopSelling',
    titleKey: 'dashboard.cards.widgetScreenTopSelling',
    group: 'widget',
    defaultLayout: { x: 3, y: 17, w: 3, h: 8, minW: 2, minH: 4 },
    defaultHidden: true,
  },
  {
    id: 'widgetRestaurantScreenTopSelling',
    titleKey: 'dashboard.cards.widgetRestaurantScreenTopSelling',
    group: 'widget',
    defaultLayout: { x: 3, y: 25, w: 3, h: 8, minW: 2, minH: 4 },
    defaultHidden: true,
  },
  {
    id: 'widgetPackageScreenTopSelling',
    titleKey: 'dashboard.cards.widgetPackageScreenTopSelling',
    group: 'widget',
    defaultLayout: { x: 3, y: 33, w: 3, h: 8, minW: 2, minH: 4 },
    defaultHidden: true,
  },

  // ===== GRAFİKLER =====
  {
    id: 'chartSalesDistribution',
    titleKey: 'dashboard.cards.chartSalesDistribution',
    group: 'chart',
    defaultLayout: { x: 0, y: 10, w: 3, h: 7, minW: 2, minH: 4 },
  },
  {
    id: 'chartBestSelling',
    titleKey: 'dashboard.cards.chartBestSelling',
    group: 'chart',
    defaultLayout: { x: 3, y: 10, w: 3, h: 7, minW: 2, minH: 4 },
  },
  {
    id: 'chartCashboxBalance',
    titleKey: 'dashboard.cards.chartCashboxBalance',
    group: 'chart',
    defaultLayout: { x: 6, y: 10, w: 3, h: 7, minW: 2, minH: 4 },
  },
  {
    id: 'chartBankBalance',
    titleKey: 'dashboard.cards.chartBankBalance',
    group: 'chart',
    defaultLayout: { x: 9, y: 10, w: 3, h: 7, minW: 2, minH: 4 },
  },
  {
    id: 'chartScreenTopSelling',
    titleKey: 'dashboard.cards.chartScreenTopSelling',
    group: 'chart',
    defaultLayout: { x: 6, y: 17, w: 3, h: 7, minW: 2, minH: 4 },
    defaultHidden: true,
  },
  {
    id: 'chartRestaurantScreenTopSelling',
    titleKey: 'dashboard.cards.chartRestaurantScreenTopSelling',
    group: 'chart',
    defaultLayout: { x: 6, y: 25, w: 3, h: 7, minW: 2, minH: 4 },
    defaultHidden: true,
  },
  {
    id: 'chartPackageScreenTopSelling',
    titleKey: 'dashboard.cards.chartPackageScreenTopSelling',
    group: 'chart',
    defaultLayout: { x: 6, y: 33, w: 3, h: 7, minW: 2, minH: 4 },
    defaultHidden: true,
  },
];

/** Varsayılan layout listesi (react-grid-layout formatında) */
export const getDefaultLayout = (): Layout[] =>
  DASHBOARD_CARDS.map((card) => ({ i: card.id, ...card.defaultLayout }));
